import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import socketIOClient from 'socket.io-client';
import axios from 'axios';
import {
	Card,
	CardContent,
	Grid,
	Typography,
	CircularProgress,
	Box
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { getUserDashboardMetrics, getUserPreferences, updateDashboardMetricsRealtime } from './store/actions/modernDashboard.actions';
import StudentMetricGauges from './components/StudentMetricGauges';
import FeaturedAppsCards from './components/FeaturedAppsCards';
import ClusterVisualization from './components/ClusterVisualization';
import PinnedApplicationsSection from './components/PinnedApplicationsSection';
import CoursesStatsSection from './components/CoursesStatsSection';
import ThemeToggle from './components/ThemeToggle';
import CampaignShowcaseDialog from './components/CampaignShowcaseDialog';
import coursesData from '../courses/coursesData.json';
import { API_BASE_URL, useWebsocket } from '@config';

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%',
		minHeight: '100vh',
		padding: theme.spacing(3),
		boxSizing: 'border-box',
		overflow: 'hidden'
	},
	rootDark: {
		backgroundColor: '#0f172a', // Dark slate background
	},
	rootLight: {
		backgroundColor: '#f1f5f9', // Light slate background
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '400px'
	},
	loadingDark: {
		color: '#94a3b8'
	},
	loadingLight: {
		color: '#64748b'
	},
	errorContainer: {
		padding: theme.spacing(3)
	},
	headerSection: {
		marginBottom: theme.spacing(4)
	},
	headerTitle: {
		fontSize: '28px',
		fontWeight: 600,
		marginBottom: theme.spacing(0.5)
	},
	headerTitleDark: {
		color: '#f8fafc'
	},
	headerTitleLight: {
		color: '#0f172a'
	},
	headerSubtitle: {
		fontSize: '14px',
		fontWeight: 400
	},
	headerSubtitleDark: {
		color: '#94a3b8'
	},
	headerSubtitleLight: {
		color: '#64748b'
	},
	sectionSpacing: {
		marginBottom: theme.spacing(3)
	},
	// Card styles for dark theme
	darkCard: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid #334155',
		boxShadow: 'none',
		'&:hover': {
			borderColor: '#475569'
		}
	},
	// Card styles for light theme
	lightCard: {
		backgroundColor: '#ffffff',
		borderRadius: '8px',
		border: '1px solid #e2e8f0',
		boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
		'&:hover': {
			borderColor: '#cbd5e1',
			boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
		}
	},
	// Layout grid
	mainGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: theme.spacing(3),
		width: '100%',
		alignItems: 'stretch',
		[theme.breakpoints.up('lg')]: {
			gridTemplateColumns: '1fr 2fr' // Fixed width for left panel, rest for map
		}
	},
	cardsColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3),
		minWidth: 0 // Allow flex item to shrink below content size
	},
	mapColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3),
		minWidth: 0, // Allow flex item to shrink below content size
		minHeight: '600px' // Ensure minimum height for map to fill
	}
}));

const UserDashboard = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	// Redux state with deep equality check to prevent unnecessary re-renders
	const { metrics, loading: metricsLoading, error: metricsError } = useSelector(
		state => state.dashboard.dashboardMetrics,
		(prev, next) => {
			// Custom equality - only re-render if data actually changed
			if (prev.loading !== next.loading) return false;
			if (prev.error !== next.error) return false;
			if (!prev.metrics && !next.metrics) return true;
			if (!prev.metrics || !next.metrics) return false;
			// Deep compare metrics
			return JSON.stringify(prev.metrics) === JSON.stringify(next.metrics);
		}
	);
	const { themeMode, loading: prefsLoading } = useSelector(
		state => state.dashboard.userPreferences
	);

	const [refreshInterval, setRefreshInterval] = useState(null);
	const [connectionStatus, setConnectionStatus] = useState({
		type: useWebsocket ? 'websocket' : 'polling',
		connected: false,
		lastUpdate: null
	});
	
	// Campaign showcase dialog state
	const [showCampaignDialog, setShowCampaignDialog] = useState(false);
	const [campaignData, setCampaignData] = useState(null);
	const user = useSelector(({ auth }) => auth.user);

	// Use refs to track previous values and only update if truly changed
	// Must be declared before any early returns
	const prevSummaryRef = useRef();
	const prevClustersRef = useRef();
	const prevWorkshopsRef = useRef();

	// Fetch data on component mount
	useEffect(() => {
		dispatch(getUserDashboardMetrics());
		dispatch(getUserPreferences());

		// Only use polling if WebSocket is disabled
		if (!useWebsocket) {
			setConnectionStatus({ type: 'polling', connected: true, lastUpdate: new Date() });
			
			const interval = setInterval(() => {
				dispatch(getUserDashboardMetrics());
				setConnectionStatus(prev => ({ ...prev, lastUpdate: new Date() }));
			}, 5000); // 5 seconds

			setRefreshInterval(interval);

			return () => {
				if (interval) {
					clearInterval(interval);
				}
			};
		} 
	}, [dispatch]);

	// Check for campaign showcase flag and load campaign data
	useEffect(() => {
		const shouldShowShowcase = localStorage.getItem('showCampaignShowcase');
		
		if (shouldShowShowcase === 'true' && user && user.campaignId) {
			// Remove flag immediately to prevent showing again
			localStorage.removeItem('showCampaignShowcase');
			
			// Fetch campaign data
			axios.get(`${API_BASE_URL}/api/campaigns/me`)
				.then(response => {
					setCampaignData(response.data);
					setShowCampaignDialog(true);
				})
				.catch(error => {
					console.error('Error fetching campaign data:', error);
				});
		}
	}, [user]);

	// Set up Socket.IO for real-time updates
	useEffect(() => {
		if (!useWebsocket) {
			return;
		}

		const socket = socketIOClient(API_BASE_URL, {
			transports: ['websocket', 'polling', 'flashsocket'],
			query: { token: window.localStorage.getItem('token') }
		});

		socket.on('connect', () => {
			setConnectionStatus({ type: 'websocket', connected: true, lastUpdate: new Date() });
		});

		socket.on('disconnect', () => {
			setConnectionStatus(prev => ({ ...prev, connected: false }));
		});

		socket.on('connect_error', (error) => {
			console.error('[ModernDashboard] ❌ WebSocket CONNECTION ERROR:', error.message);
			console.error('[ModernDashboard] - Error details:', error);
			setConnectionStatus({ type: 'websocket', connected: false, error: error.message });
		});

		// Listen for dashboard metrics updates
		socket.on('dashboard:metrics:update', (data) => {
			setConnectionStatus(prev => ({ ...prev, lastUpdate: new Date() }));
			if (data && data.summary) {
				dispatch(updateDashboardMetricsRealtime(data));
			}
		});

		// Listen for generic change events (used by original dashboard)
		// NOTE: Don't refetch on change events - we already get real-time updates above
		socket.on('change', (data) => {
			// DO NOT call getDashboardMetrics() here - causes loading flash
			// The dashboard:metrics:update event handles real-time updates
		});

		return () => {
			socket.disconnect();
		};
	}, [dispatch]);

	// Memoize props to prevent unnecessary re-renders of child components
	// Must be before early returns to follow hooks rules
	const memoizedSummary = useMemo(() => {
		if (!metrics || !metrics.summary) return null;
		const summaryStr = JSON.stringify(metrics.summary);
		const prevStr = JSON.stringify(prevSummaryRef.current);
		if (summaryStr === prevStr) {
			return prevSummaryRef.current; // Return same reference
		}
		prevSummaryRef.current = metrics.summary;
		return metrics.summary;
	}, [metrics]);
	
	const memoizedClusters = useMemo(() => {
		if (!metrics || !metrics.clusters) return [];
		const clustersStr = JSON.stringify(metrics.clusters);
		const prevStr = JSON.stringify(prevClustersRef.current);
		if (clustersStr === prevStr) {
			return prevClustersRef.current; // Return same reference
		}
		prevClustersRef.current = metrics.clusters;
		return metrics.clusters;
	}, [metrics]);
	
	const memoizedWorkshops = useMemo(() => {
		if (!metrics || !metrics.workshops) return [];
		const workshopsStr = JSON.stringify(metrics.workshops);
		const prevStr = JSON.stringify(prevWorkshopsRef.current);
		if (workshopsStr === prevStr) {
			return prevWorkshopsRef.current; // Return same reference
		}
		prevWorkshopsRef.current = metrics.workshops;
		return metrics.workshops;
	}, [metrics]);

	// Loading state
	if (metricsLoading || prefsLoading) {
		const isDark = themeMode === 'dark';
		return (
			<Box className={`${classes.loadingContainer} ${isDark ? classes.loadingDark : classes.loadingLight}`}>
				<CircularProgress size={60} />
				<Typography variant="h6" style={{ marginTop: 20 }}>
					Loading Dashboard...
				</Typography>
			</Box>
		);
	}

	// Error state
	if (metricsError) {
		return (
			<Box className={classes.errorContainer}>
				<Alert severity="error">
					<Typography variant="h6">Failed to load dashboard metrics</Typography>
					<Typography variant="body2">{metricsError}</Typography>
				</Alert>
			</Box>
		);
	}

	// No data state
	if (!metrics) {
		return (
			<Box className={classes.errorContainer}>
				<Alert severity="info">
					<Typography variant="h6">No metrics available</Typography>
					<Typography variant="body2">
						Dashboard metrics are being aggregated. Please check back in a moment.
					</Typography>
				</Alert>
			</Box>
		);
	}

	const isDark = themeMode === 'dark';

	return (
		<div className={`${classes.root} ${isDark ? classes.rootDark : classes.rootLight}`}>
			{/* Main Grid Layout: Metrics (1/3) | Visualization (2/3) */}
			<div className={classes.mainGrid}>
			{/* Left Column: Student Promo + Featured Apps */}
			<div className={classes.cardsColumn}>
				<StudentMetricGauges courses={coursesData} themeMode={themeMode} />
				<FeaturedAppsCards themeMode={themeMode} />
			</div>				{/* Right Column: Tabbed Cluster Visualization (Map/List) */}
				<div className={classes.mapColumn}>
					<ClusterVisualization clusters={memoizedClusters} themeMode={themeMode} userView={true} user={user} />
				</div>
			</div>

			{/* Courses Stats Section - Horizontal Scroll */}
			<CoursesStatsSection courses={coursesData} themeMode={themeMode} />

			{/* Pinned Applications - macOS Dock Style (Always Visible in User Dashboard) */}
			<PinnedApplicationsSection workshops={metrics.workshops} alwaysVisible={true} campaignData={campaignData} />

			{/* Campaign Showcase Dialog */}
			<CampaignShowcaseDialog
				open={showCampaignDialog}
				onClose={() => setShowCampaignDialog(false)}
				campaignData={campaignData}
			/>
		</div>
	);
};

export default UserDashboard;